import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNav from '../components/SiteNav';
import { useTema } from '../hooks/useTema';

function ItalianFlag({ size = 28, alt }) {
  const w = size, h = Math.round(size * 2 / 3);
  return (
    <svg
      width={w} height={h} viewBox="0 0 3 2"
      xmlns="http://www.w3.org/2000/svg"
      role="img" aria-label={alt}
      className="cps-flag-svg"
    >
      <rect width="1" height="2" x="0" fill="#009246" />
      <rect width="1" height="2" x="1" fill="#ffffff" />
      <rect width="1" height="2" x="2" fill="#CE2B37" />
    </svg>
  );
}

const DIFF_ICONS = ['💶', '🔐', '✨', '⚖️'];
const TEAM_ICONS = ['👤', '📣', '⚖️', '💻', '🎧'];

export default function AboutPage({ onLogin, onRegister, onNavigateHome, onNews, onTerms, onPrivacy }) {
  const { t } = useTranslation('about');
  // Stessa definizione usata dalla home: hooks/useTema.js.
  const { tema, cambiaTema } = useTema();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Le voci di sezione sono ancore della home: da qui si naviga, non si scorre.
  // E' l'unica differenza di comportamento fra questa nav e quella della home,
  // ed e' il motivo per cui SiteNav prende onVaiA come parametro invece di
  // decidere da se'.
  function goHome(hash) { return onNavigateHome(hash); }

  function vaiA(id) {
    return goHome(id === 'hero' ? undefined : id);
  }

  return (
    // cps-home-root porta con se' la palette chiara (App.css ~riga 2253). Il
    // nome resta quello della home finche' lo scope non si sposta al guscio
    // dell'applicazione, che e' l'ultimo passo di questa migrazione: cambiarlo
    // adesso vorrebbe dire riscrivere ~40 selettori due volte.
    <div className="cps-root">
      <div className="cloud-layer" aria-hidden="true">
        <div className="cloud c1"/><div className="cloud c2"/>
        <div className="cloud c3"/><div className="cloud c4"/>
        <div className="cloud c5"/><div className="cloud c6"/>
        <div className="cloud c7"/><div className="cloud c8"/>
        <div className="cloud c9"/><div className="cloud c10"/>
      </div>

      {/* NAV — barra unica, vedi components/SiteNav.jsx. Prima era una copia
          divergente: niente EN/IT, niente selettore di tema, niente Roadmap,
          hamburger col carattere ☰ ed emoji 🆕 accanto a Novita'. */}
      <SiteNav
        pagina="about"
        onVaiA={vaiA}
        onAbout={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onNews={onNews}
        onLogin={onLogin}
        onRegister={onRegister}
        tema={tema}
        onCambiaTema={cambiaTema}
      />

      {/* INTRO / MISSION */}
      <section className="cps-hero" style={{ minHeight:'auto', paddingTop:160, paddingBottom:70 }}>
        <div className="cps-hero-badge">{t('hero_badge')}</div>
        <h1 className="cps-hero-title" style={{ fontSize:'clamp(36px,6vw,64px)' }}>
          {t('hero_title_line1')}<br/>
          <span className="cps-hero-green">{t('hero_title_line2')}</span>
        </h1>
        <p className="cps-hero-sub" style={{ maxWidth:640 }}>
          {t('hero_sub_p1')} <strong>{t('hero_sub_strong')}</strong>{t('hero_sub_p2')}
        </p>
      </section>

      {/* PROUDLY ITALIAN */}
      <section className="cps-section cps-section-dark">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('italian_label')}</div>
          <h2 className="cps-section-title cps-about-flag-title">
            <ItalianFlag size={30} alt={t('flag_alt')} />
            {t('italian_title')}
          </h2>
          <p className="cps-section-sub" style={{ maxWidth:640, marginBottom:0 }}>
            {t('italian_body')}
          </p>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className="cps-section">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('diff_label')}</div>
          <h2 className="cps-section-title">{t('diff_title')}</h2>
          <p className="cps-section-sub">{t('diff_sub')}</p>
          <div className="cps-roadmap-grid">
            {DIFF_ICONS.map((icon, i) => (
              <div key={i} className="cps-roadmap-card">
                <span className="cps-roadmap-icon">{icon}</span>
                <h4>{t(`diff_${i+1}_title`)}</h4>
                <p>{t(`diff_${i+1}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="cps-section cps-section-dark">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('team_label')}</div>
          <h2 className="cps-section-title">{t('team_title')}</h2>
          <p className="cps-section-sub">
            {t('team_sub')}
          </p>
          <div className="cps-roadmap-grid">
            {TEAM_ICONS.map((icon, i) => (
              <div key={i} className="cps-roadmap-card">
                <span className="cps-roadmap-icon">{icon}</span>
                <h4>{t(`team_${i+1}_role`)}</h4>
                <p>{t(`team_${i+1}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="cps-section">
        <div className="cps-section-inner cps-about-closing">
          <div className="cps-section-label">{t('closing_label')}</div>
          <h2 className="cps-section-title">{t('closing_title')}</h2>
          <p>
            {t('closing_body')}
          </p>
          <button className="cps-cta-primary" onClick={onRegister}>{t('closing_cta')}</button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="cps-footer">
        <div className="cps-footer-inner">
          <div className="cps-footer-brand">
            <span>♾️ LifetimeCloud.me</span>
            <p>{t('footer_tagline')}</p>
          </div>
          <div className="cps-footer-links">
            <button onClick={() => goHome('how-it-works')}>{t('nav_how_it_works')}</button>
            <button onClick={() => goHome('plans')}>{t('nav_plans')}</button>
            <button onClick={() => goHome('security')}>{t('nav_security')}</button>
            <button onClick={() => goHome('faq')}>{t('nav_faq')}</button>
            <button onClick={onLogin}>{t('nav_login')}</button>
            <button onClick={onRegister}>{t('nav_register')}</button>
            <button onClick={onTerms}>{t('common:footer_terms')}</button>
            <button onClick={onPrivacy}>{t('common:footer_privacy')}</button>
          </div>
        </div>
        <div className="cps-footer-bottom">
          <span>{t('footer_copyright')}</span>
          <span>🔒 {t('footer_security_badge')}</span>
        </div>
      </footer>
    </div>
  );
}
